import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import {
  LANGUAGE_STORAGE_KEY,
  PSEUDO_LANGUAGE_CODE,
  getSupportedLanguageCodes,
  normalizeLanguageCode,
} from './languages';

const isDev = import.meta.env.DEV;
const supportedLngs = getSupportedLanguageCodes({ includePseudo: isDev });

const PSEUDO_CHAR_MAP = Object.freeze({
  a: 'á',
  b: 'ƀ',
  c: 'ç',
  d: 'ď',
  e: 'ë',
  f: 'ƒ',
  g: 'ğ',
  h: 'ħ',
  i: 'ï',
  j: 'ĵ',
  k: 'ķ',
  l: 'ľ',
  m: 'ɱ',
  n: 'ñ',
  o: 'ô',
  p: 'ƥ',
  q: 'ɋ',
  r: 'ř',
  s: 'ş',
  t: 'ŧ',
  u: 'ü',
  v: 'ṽ',
  w: 'ŵ',
  x: 'ẋ',
  y: 'ÿ',
  z: 'ž',
});

let pseudoLocaleActive = false;
const missingTranslationWarnings = new Set();

function pseudoTransform(value) {
  if (!pseudoLocaleActive || typeof value !== 'string' || value.length === 0) {
    return value;
  }

  const parts = value.split(/(\{\{[^}]+\}\})/g);
  const transformed = parts
    .map((part) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        return part;
      }

      return part.replace(/[A-Za-z]/g, (char) => {
        const lower = char.toLowerCase();
        const mapped = PSEUDO_CHAR_MAP[lower] || char;
        return char === lower ? mapped : mapped.toUpperCase();
      });
    })
    .join('');

  return `［${transformed}］`;
}

function warnMissingTranslationKey(key) {
  if (!isDev) {
    return key;
  }

  const locale = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language, {
    includePseudo: true,
  });
  const signature = `${locale}:${key}`;

  if (missingTranslationWarnings.has(signature)) {
    return key;
  }

  missingTranslationWarnings.add(signature);
  console.warn(`[i18n] Missing translation key "${key}" for locale "${locale}"`);
  return key;
}

function syncDocumentLanguage(language) {
  if (typeof document === 'undefined') {
    return;
  }

  const normalized = normalizeLanguageCode(language, { includePseudo: isDev });
  document.documentElement.lang = normalized;
}

function syncLanguageQueryParam(language) {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = normalizeLanguageCode(language, { includePseudo: isDev });
  const url = new URL(window.location.href);

  if (normalized === 'en') {
    url.searchParams.delete('lang');
  } else {
    url.searchParams.set('lang', normalized);
  }

  const nextPath = `${url.pathname}${url.search}${url.hash}`;
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextPath !== currentPath) {
    window.history.replaceState(window.history.state, '', nextPath);
  }
}

const pseudoPostProcessor = {
  name: 'cedrumPseudo',
  type: 'postProcessor',
  process: pseudoTransform,
};

if (!i18n.isInitialized) {
  i18n
    .use(pseudoPostProcessor)
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      showSupportNotice: false,
      fallbackLng: 'en',
      supportedLngs,
      nonExplicitSupportedLngs: true,
      load: 'languageOnly',
      ns: ['common'],
      defaultNS: 'common',
      returnNull: false,
      parseMissingKeyHandler: warnMissingTranslationKey,
      interpolation: {
        escapeValue: false,
      },
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
      },
      detection: {
        order: ['querystring', 'localStorage', 'navigator'],
        lookupQuerystring: 'lang',
        lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        caches: ['localStorage'],
        convertDetectedLanguage: (detected) =>
          normalizeLanguageCode(detected, { includePseudo: isDev }),
      },
      postProcess: ['cedrumPseudo'],
    });
}

pseudoLocaleActive =
  isDev && normalizeLanguageCode(i18n.language, { includePseudo: true }) === PSEUDO_LANGUAGE_CODE;
syncDocumentLanguage(i18n.language);

i18n.on('languageChanged', (lng) => {
  pseudoLocaleActive =
    isDev && normalizeLanguageCode(lng, { includePseudo: true }) === PSEUDO_LANGUAGE_CODE;
  syncDocumentLanguage(lng);
  syncLanguageQueryParam(lng);
});

export default i18n;
